import type {JSX} from 'react';

import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$createParagraphNode, $getRoot} from 'lexical';
import * as React from 'react';

/**
 * ClearContentPlugin
 * 
 * A Lexical plugin that provides a function to clear all editor content.
 * This plugin resets the editor to an empty state with a single empty paragraph.
 * 
 * Features:
 * - Programmatic content clearing
 * - Resets to clean initial state
 * - Maintains editor focus and cursor position
 * - Consistent with other content management plugins
 */
export default function ClearContentPlugin({
  onClear,
}: {
  onClear: React.MutableRefObject<() => void>;
}): JSX.Element {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    const clearContent = (): void => {
      editor.update(() => {
        const root = $getRoot();
        root.clear();
        const paragraph = $createParagraphNode();
        root.append(paragraph);
        paragraph.select(); // Set cursor to the new paragraph
      });
    };

    // Assign the clearContent function to the ref
    onClear.current = clearContent;
  }, [editor, onClear]);

  // This plugin doesn't render anything visible
  return <></>;
}
